import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Logo, Icon } from './Brand'
import { initialsFromName, roleLabel } from '../lib/user'

const demoNav = [
  { id: 'dashboard', label: 'Dashboard', icon: 'space_dashboard' },
  { id: 'agents', label: 'Agents', icon: 'smart_toy', badgeTone: 'teal' },
  { id: 'build', label: 'Build', icon: 'construction' },
  { id: 'evaluate', label: 'Evaluate', icon: 'fact_check' },
  { id: 'chat', label: 'Chat', icon: 'forum' },
  { id: 'controls', label: 'Controls', icon: 'tune' },
]

const collectionsNav = [
  { id: 'collections-overview', label: 'Overview', icon: 'space_dashboard' },
  { id: 'collections-work-queue', label: 'Work queue', icon: 'view_list' },
  { id: 'collections-cash-flow', label: 'Cash flow', icon: 'monitoring' },
  { id: 'collections-escalations', label: 'Escalations', icon: 'crisis_alert' },
  { id: 'collections-chat', label: 'Collect-it', icon: 'auto_awesome' },
]

export default function Sidebar({
  activeView = 'dashboard',
  onNavigate = () => {},
  notifCount = 0,
  approvalCount = 0,
  agentCount = 0,
  user = null,
  onLogout = () => {},
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const userRef = useRef(null)
  const isCollections = activeView.startsWith('collections-')
  const nav = isCollections ? collectionsNav : demoNav

  // Close the user menu on outside-click or Escape.
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setMenuOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  return (
    <motion.aside
      className="sidebar"
      initial={{ x: -24, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="sb-logo">
        <Logo height={26} />
        <span className="brand-by">by Commit</span>
      </div>

      <div className="sb-workspace-switcher">
        <button className={!isCollections ? 'active' : ''} onClick={() => onNavigate('dashboard')}><Icon name="smart_toy" />Agents</button>
        <button className={isCollections ? 'active' : ''} onClick={() => onNavigate('collections-overview')}><Icon name="account_balance" />Collections</button>
      </div>

      <div className="sb-section">{isCollections ? 'Collections' : 'Workspace'}</div>
      {nav.map((item) => {
        const active = item.id === activeView
        // The agents badge reflects the live registry count; others are static.
        const badge = item.id === 'agents' ? (agentCount || null) : item.badge
        return (
          <motion.div
            key={item.id}
            className={`nav-item ${active ? 'active' : ''}`}
            onClick={() => onNavigate(item.id)}
            whileHover={{ x: active ? 0 : 3 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          >
            <Icon name={item.icon} />
            {item.label}
            {badge && <span className={`nav-badge ${item.badgeTone || ''}`}>{badge}</span>}
          </motion.div>
        )
      })}

      {isCollections && <>
        <div className="sb-section">Manage</div>
        <motion.div
          className={`nav-item ${activeView === 'collections-operations' ? 'active' : ''}`}
          onClick={() => onNavigate('collections-operations')}
          whileHover={{ x: activeView === 'collections-operations' ? 0 : 3 }}
        >
          <Icon name="admin_panel_settings" />Operations & admin
        </motion.div>
      </>}

      {!isCollections && <>
      <div className="sb-section">Activity</div>
      <motion.div
        className={`nav-item ${activeView === 'notifications' ? 'active' : ''}`}
        onClick={() => onNavigate('notifications')}
        whileHover={{ x: activeView === 'notifications' ? 0 : 3 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
      >
        <Icon name="notifications" />
        Notifications
        {notifCount > 0 && (
          <motion.span
            className="nav-badge"
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          >
            {notifCount}
          </motion.span>
        )}
      </motion.div>
      <motion.div
        className={`nav-item ${activeView === 'approvals' ? 'active' : ''}`}
        onClick={() => onNavigate('approvals')}
        whileHover={{ x: activeView === 'approvals' ? 0 : 3 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
      >
        <Icon name="receipt_long" />
        Approvals
        {approvalCount > 0 && <span className="nav-badge teal">{approvalCount}</span>}
      </motion.div>
      </>}

      <div className="sb-spacer" />

      <div className="sb-user-wrap" ref={userRef}>
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              className="sb-user-menu"
              role="menu"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="sb-user-menu-id">@{user?.user_id || 'guest'}</div>
              <button className="sb-user-menu-item" role="menuitem" onClick={onLogout}>
                <Icon name="logout" />Log out
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="button"
          className="sb-user"
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <div className="avatar" style={{ background: 'var(--coral-400)' }}>
            {initialsFromName(user?.name)}
          </div>
          <div className="who">
            <b>{user?.name || 'Guest'}</b>
            <span>{roleLabel(user?.role)} · {isCollections ? 'Commit Collections' : 'Acme Inc'}</span>
          </div>
          <Icon
            name="expand_more"
            className="sb-user-chev"
            style={{
              marginLeft: 'auto', color: 'var(--text-tertiary)', fontSize: 20,
              transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform .18s',
            }}
          />
        </button>
      </div>
    </motion.aside>
  )
}
